import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface NavArrowButtonProps {
  direction: "prev" | "next";
  onClick?: () => void;
  disabled?: boolean;
  // Accessible label (icon-only button).
  label: string;
  // Optional tooltip content; when set, the button is wrapped in a tooltip.
  tooltip?: React.ReactNode;
}

// Segmented `secondary` prev/next arrow button, matching the Call Details
// header control. Render a `prev` and a `next` side by side inside a
// `flex items-center` wrapper to form one joined segment: `prev` squares its
// right edge, `next` squares its left edge and adds the dividing border.
export const NavArrowButton = ({
  direction,
  onClick,
  disabled = false,
  label,
  tooltip,
}: NavArrowButtonProps): React.ReactElement => {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;

  const button = (
    <Button
      variant="secondary"
      size="icon"
      className={cn(
        "h-8 w-8",
        direction === "prev"
          ? "rounded-r-none"
          : "rounded-l-none border-l border-border"
      )}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );

  if (!tooltip) return button;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
